import type { TFunction } from "i18next";
import i18n from "i18next";
import type { NotificationDto } from "@api/modules/notifications.api";

const APP_ROUTES = new Set([
    "areas",
    "riders",
    "products",
    "stocks",
    "customers",
    "payments",
    "shifts",
    "deliveries",
    "movements",
    "reports",
    "notifications",
    "support",
    "subscription",
    "profile",
    "settings",
]);

/**
 * Notifications carry an API-style path (`/tenant/shifts`, `/tenant/billing`). The app's own routes
 * drop the `/tenant` prefix, and some paths belong to the rider or customer apps — those give null.
 */
export function appPathFromApiUrl(url: string | undefined | null): string | null {
    if (!url || typeof url !== "string") return null;
    const trimmed = url.trim().replace(/^\/+/, "");
    if (!trimmed || /^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return null;
    const rest = trimmed.startsWith("tenant/") ? trimmed.slice("tenant/".length) : trimmed;
    if (rest === "tenant" || rest === "") return "/";
    const [first, ...tail] = rest.split("/");
    if (first === "billing") return "/subscription";
    if (!APP_ROUTES.has(first)) return null;
    return `/${[first, ...tail].join("/")}`;
}

/** Event parameters arrive as snake_case on the stream and camelCase in lists. */
export function notificationParams(n: Pick<NotificationDto, "data">): Record<string, string> {
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(n.data ?? {})) {
        if (value === null || typeof value === "object") continue;
        const camel = key.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
        out[camel] = String(value);
    }
    return out;
}

/** Where a notification should take the reader, or null to stay on the notifications page. */
export function notificationTarget(n: NotificationDto): string | null {
    const p = notificationParams(n);
    switch (n.code) {
        case "shift.opened":
            return p.shiftId ? `/shifts/${p.shiftId}` : "/shifts";
        case "customer.created":
            return p.customerId ? `/customers/${p.customerId}` : "/customers";
        case "rider.created":
            return p.riderId ? `/riders/${p.riderId}` : "/riders";
        case "delivery.recorded":
            return "/deliveries";
        case "subscription.payment_due":
        case "subscription.suspended":
            return "/subscription";
        default:
            return appPathFromApiUrl(n.url);
    }
}

/**
 * Title and message in the reader's language when the event has a stable `code` we know;
 * otherwise the server's own English text.
 */
export function notificationText(t: TFunction, n: NotificationDto): { title: string; message: string } {
    if (n.code) {
        // Dots would read as nesting in a translation key, so `delivery.recorded` is stored as `delivery_recorded`.
        const id = n.code.replaceAll(".", "_");
        const titleKey = `notifications.codes.${id}.title`;
        const messageKey = `notifications.codes.${id}.message`;
        if (i18n.exists(titleKey)) {
            const params = notificationParams(n);
            return {
                title: t(titleKey, { ...params, defaultValue: n.title }) as string,
                message: i18n.exists(messageKey)
                    ? (t(messageKey, { ...params, defaultValue: n.message }) as string)
                    : n.message,
            };
        }
    }
    return { title: n.title, message: n.message };
}
